import { Box, ListItem, MenuItem, Modal, Select, SelectChangeEvent, Chip } from '@mui/material'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { useTranslation } from 'react-i18next'
import React, { FormEvent, useEffect, useState } from 'react'
import styles from './Editor.module.css'
import { Typography } from '@mui/material'
import { getAppContainer, STATUS_SERVICE_KEY } from '../../container/app'
import { IStatusService } from '../../common/status/StatusService'
import { Query, QueryParam } from '../../common/api/Query'
import { getTrainingContainer } from '../../container/training-module'
import {
  ITRainingService,
  IUserTrainingService,
  TRAINING_SERVICE_KEY,
  USER_TRAINING_SERVICE_KEY,
} from '../../modules/training'
import { TrainingDTO } from '../../modules/training/models/TrainingDTO'
import { fromModel as fromModelUserTraining } from '../../modules/training/models/UserTrainingDTO'
import stylesCommon from '../../components/modal/CustomModal.module.css'
import {
  emptyUserTrainingDTO,
  UserTrainingDTO,
} from '../../modules/training/models/UserTrainingDTO'
import { UserTraining } from '../../modules/training/models/UserTraining'
import { v4 as uuidv4 } from 'uuid'
import { Input } from '../../pages/user-profile/userProfileStylesMui'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TextField } from '@mui/material'
import calendarIcon from '../../assets/calendar/calendar-newEvent.svg'
import {
  DurationBadge,
  TRAINING_DURATION_BADGES,
  createDurationBadges,
  calculateDuration,
  findMatchingBadge,
  getDurationText,
  calculateEndDateFromBadge,
} from '../../common/utils/durationBadgeUtils'
import dayjs from 'dayjs'

const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)
const userTrainingService =
  getTrainingContainer().get<IUserTrainingService>(USER_TRAINING_SERVICE_KEY)
const trainingService = getTrainingContainer().get<ITRainingService>(TRAINING_SERVICE_KEY)

type AssignTrainingFormProps = {
  id?: string
  trainingID?: string
  open: boolean
  handleClose: () => void
}

const convertToDate = (date: any): Date => {
  if (!date) return new Date()
  if (date instanceof Date) return date
  if (typeof date === 'string') return new Date(date)
  return new Date()
}

const CalendarIcon = () => (
  <img src={calendarIcon} alt="calendar" style={{ marginRight: 8, pointerEvents: 'none' }} />
)

const datePickerSx = {
  '& fieldset': {
    borderRadius: 32,
    border: '1px solid var(--gray-soft)',
  },
  '& .MuiOutlinedInput-root': {
    border: 'none',
    '&.Mui-focused fieldset': {
      borderColor: 'var(--green-light)',
    },
  },
}

export function AssignTrainingForm(props: AssignTrainingFormProps) {
  const { t } = useTranslation()
  const [training, setTraining] = useState<TrainingDTO[]>([])
  const [userTraining, setUserTraining] = useState<UserTrainingDTO>(emptyUserTrainingDTO())
  const [userTrainingArray, setUserTrainingArray] = useState<UserTrainingDTO[]>()
  const [selected, setSelected] = useState<string>()
  const [series, setSeries] = useState<number>(0)
  const [repetitions, setRepetitions] = useState<number>(0)
  const [frequency, setFrequency] = useState<string>('')
  const [currentTraining, setCurrentTraining] = useState<TrainingDTO>()
  const [DateTimePickerError, setDateTimePickerError] = useState<string>('')
  const [selectedBadge, setSelectedBadge] = useState<string | null>(null)
  const [durationText, setDurationText] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const durationBadges = createDurationBadges(TRAINING_DURATION_BADGES, t)

  useEffect(() => {
    trainingService.getFilteredList(new Query({ query: [] })).subscribe((res) => {
      setTraining(res.items)
    })
  }, [])

  useEffect(() => {
    if (!props.id) return
    userTrainingService
      .getFilteredList(
        new Query({
          query: [new QueryParam<UserTraining>('userID', props.id)],
        })
      )
      .subscribe((res) => {
        setUserTrainingArray(res.items)
      })
  }, [props.id])

  useEffect(() => {
    if (!props.trainingID) return

    userTrainingService.getByID(props.trainingID).subscribe((res) => {
      if (!res) return
      const userTrainingData = fromModelUserTraining(res)
      setUserTraining(userTrainingData)

      setSeries(res.series || 0)
      setRepetitions(res.repetitions || 0)

      if (res.frecuency) {
        if (res.frecuency === 1) setFrequency('morning')
        else if (res.frecuency === 2) setFrequency('afternoon')
        else if (res.frecuency === 3) setFrequency('night')
      }

      if (res.trainingID) {
        trainingService.getByID(res.trainingID).subscribe((trainingRes) => {
          if (!trainingRes) return
          setCurrentTraining(trainingRes)
          setSelected(trainingRes.title)
        })
      }
    })
  }, [props.trainingID])

  useEffect(() => {
    if (userTraining.startDate) {
      const calculatedDuration = calculateDuration(
        userTraining.startDate,
        userTraining.finishDate,
        false
      )
      const text = getDurationText(calculatedDuration, true, false, t, 'training')
      setDurationText(text)
      const matchingBadgeId = findMatchingBadge(
        calculatedDuration,
        TRAINING_DURATION_BADGES,
        !!userTraining.finishDate,
        false
      )
      setSelectedBadge(matchingBadgeId)
    } else {
      const text = getDurationText(0, false, false, t, 'training')
      setDurationText(text)
      setSelectedBadge(null)
    }
  }, [userTraining.startDate, userTraining.finishDate, t])

  const handleBack = () => props.handleClose()

  const handleTrainingSelected = (e: SelectChangeEvent<string>) => {
    const value = e.target.value
    setCurrentTraining(Object.assign({ title: value }))
    setSelected(value)
  }

  const handleStartDateChange = (event: any) => {
    if (event) {
      setUserTraining((prev) => ({ ...prev, startDate: event.toDate() }))
    }
  }

  const handleFinishDateChange = (event: any) => {
    if (event) {
      setUserTraining((prev) => ({ ...prev, finishDate: event.toDate() }))
    }
  }

  const handleBadgeClick = (badge: DurationBadge) => {
    if (!userTraining.startDate) return

    if (selectedBadge === badge.id) {
      setSelectedBadge(null)
      setUserTraining((prev) => {
        const defaultEndDate = new Date(prev.startDate)
        defaultEndDate.setDate(defaultEndDate.getDate() + 7)
        return { ...prev, finishDate: defaultEndDate }
      })
    } else {
      setSelectedBadge(badge.id)
      setUserTraining((prev) => {
        const startDate = convertToDate(prev.startDate)
        const endDate = calculateEndDateFromBadge(startDate, badge.days)
        return { ...prev, finishDate: endDate }
      })
    }
  }

  const getFrequencyNumber = (freq: string): number => {
    if (freq === 'morning') return 1
    if (freq === 'afternoon') return 2
    if (freq === 'night') return 3
    return 0
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!currentTraining?.title && !props.trainingID) {
      statusService.sendStatus({ variant: 'warning', message: t('trainingNotSelected') })
      return
    }
    if (series === 0) {
      statusService.sendStatus({ variant: 'warning', message: t('seriesZero') })
      return
    }
    if (repetitions === 0) {
      statusService.sendStatus({ variant: 'warning', message: t('repetitionsZero') })
      return
    }
    if (!frequency && !props.trainingID) {
      statusService.sendStatus({ variant: 'warning', message: t('frequencyNotSelected') })
      return
    }

    let auxID = ''
    training.forEach((item) => {
      if (selected === item.title) auxID = item.id
    })

    const trainingIDToUse = props.trainingID ? userTraining.trainingID : auxID
    const frequencyNumber = getFrequencyNumber(frequency)

    const newTrainingAssign: UserTrainingDTO = {
      id: props.trainingID ? props.trainingID : uuidv4(),
      startDate: userTraining.startDate,
      finishDate: userTraining.finishDate,
      series: series,
      repetitions: repetitions,
      trainingID: trainingIDToUse,
      userID: props.id || '',
      completed: props.trainingID ? userTraining.completed : false,
      frecuency: frequencyNumber,
    }

    setIsSubmitting(true)

    if (props.trainingID) {
      userTrainingService.update(newTrainingAssign).subscribe((res) => {
        if (res) {
          handleBack()
        } else {
          setIsSubmitting(false)
        }
      })
    } else {
      userTrainingService.add(newTrainingAssign).subscribe((res) => {
        if (res) {
          handleBack()
        } else {
          setIsSubmitting(false)
        }
      })
    }
  }

  return (
    <Modal
      open={props.open}
      onClose={props.handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ overflow: 'auto' }}
    >
      <Box className={stylesCommon.agoraContainer}>
        <Box sx={{ padding: 2, width: '90%', maxWidth: 800, margin: '20px auto' }}>
          <Box
            sx={{ borderRadius: '32px', background: '#f9f9f9', width: '100%', padding: '8px 0' }}
          >
            <h4 className={styles.modalHeader}>
              {props.trainingID ? t('editTraining') : t('assignTraining')}
            </h4>

            <form onSubmit={handleSave}>
              <Box className={styles.form}>
                <Box className={styles.formSection}>
                  <Box width="100%">
                    <Typography className={styles.label}>
                      {currentTraining
                        ? t(currentTraining?.title) || ''
                        : userTrainingArray?.length === training.length
                        ? t('allAssignTrainings')
                        : t('training')}
                    </Typography>
                    <Select
                      variant={'outlined'}
                      sx={{ width: '100%' }}
                      id="title"
                      name={'title'}
                      value={currentTraining?.title || ''}
                      label={''}
                      className={styles.frequencySelect}
                      disabled={userTrainingArray?.length === training.length || !!props.trainingID}
                      onChange={handleTrainingSelected}
                    >
                      {userTrainingArray &&
                        training.map((item) => {
                          const filter = userTrainingArray.filter(
                            (itemUT) => itemUT.trainingID === item.id
                          )
                          if (!filter.length || props.trainingID) {
                            return (
                              <MenuItem key={item.id} value={item.title}>
                                {t(item.title)}
                              </MenuItem>
                            )
                          }
                          return null
                        })}
                    </Select>
                  </Box>
                </Box>

                <Box className={styles.fieldsRow}>
                  <ListItem className={styles.fieldItem}>
                    <Box width="100%">
                      <Typography className={styles.label}>{t('startDate')}</Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                        <DatePicker
                          key={'startDate'}
                          inputFormat={'DD/MM/YYYY'}
                          minDate={new Date()}
                          onChange={handleStartDateChange}
                          value={userTraining.startDate ? dayjs(userTraining.startDate) : null}
                          label={''}
                          onError={(reason) => {
                            switch (reason) {
                              case 'invalidDate':
                                setDateTimePickerError(t('invalidDateMessage'))
                                break
                              case 'minDate':
                                setDateTimePickerError(t('minDateMessage'))
                                break
                              default:
                                setDateTimePickerError('')
                            }
                          }}
                          renderInput={(props) => (
                            <TextField
                              id={'startDate'}
                              size={'small'}
                              sx={datePickerSx}
                              helperText={props.error && DateTimePickerError}
                              {...props}
                            />
                          )}
                          components={{
                            OpenPickerIcon: CalendarIcon,
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                  </ListItem>
                  <ListItem className={styles.fieldItem}>
                    <Box width="100%">
                      <Typography className={styles.label}>{t('finishDate')}</Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                        <DatePicker
                          key={'finishDate'}
                          inputFormat={'DD/MM/YYYY'}
                          value={userTraining.finishDate ? dayjs(userTraining.finishDate) : null}
                          minDate={
                            userTraining.startDate ? dayjs(userTraining.startDate) : undefined
                          }
                          onChange={handleFinishDateChange}
                          label={''}
                          onError={(reason) => {
                            switch (reason) {
                              case 'invalidDate':
                                setDateTimePickerError(t('invalidDateMessage'))
                                break
                              case 'minDate':
                                setDateTimePickerError(t('minDateMessage'))
                                break
                              default:
                                setDateTimePickerError('')
                            }
                          }}
                          renderInput={(props) => (
                            <TextField
                              id={'finishDate'}
                              size={'small'}
                              sx={datePickerSx}
                              helperText={props.error && DateTimePickerError}
                              {...props}
                            />
                          )}
                          components={{
                            OpenPickerIcon: CalendarIcon,
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                  </ListItem>
                </Box>

                <Box className={styles.durationSection}>
                  <Box display={'flex'} alignItems={'center'}>
                    <Typography variant="body2" className={styles.durationText}>
                      {durationText}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    {durationBadges.map((badge) => (
                      <Chip
                        key={badge.id}
                        label={badge.label}
                        clickable
                        onClick={() => handleBadgeClick(badge)}
                        color={selectedBadge === badge.id ? 'primary' : 'default'}
                        variant={selectedBadge === badge.id ? 'filled' : 'outlined'}
                        className={selectedBadge === badge.id ? styles.selectedBadge : styles.badge}
                      />
                    ))}
                  </Box>
                </Box>

                <Box className={styles.fieldsRow}>
                  <ListItem className={styles.fieldItem}>
                    <Box width="100%">
                      <Typography className={styles.label}>{t('series')}</Typography>
                      <Input
                        key={'series'}
                        label={''}
                        placeholder={t('seriesPlaceholder')}
                        type="number"
                        id={'series'}
                        name={'series'}
                        value={series}
                        onChange={(e) => setSeries(Number(e.target.value))}
                        required={true}
                        fullWidth={true}
                        size={'small'}
                        variant={'outlined'}
                      />
                    </Box>
                  </ListItem>
                  <ListItem className={styles.fieldItem}>
                    <Box width="100%">
                      <Typography className={styles.label}>{t('repetitions')}</Typography>
                      <Input
                        key={'repetitions'}
                        label={''}
                        placeholder={t('repetitionsPlaceholder')}
                        type="number"
                        id={'repetitions'}
                        name={'repetitions'}
                        value={repetitions}
                        onChange={(e) => setRepetitions(Number(e.target.value))}
                        required={true}
                        fullWidth={true}
                        size={'small'}
                        variant={'outlined'}
                      />
                    </Box>
                  </ListItem>
                </Box>

                <Box width="100%">
                  <Typography className={styles.label}>{t('frequency')}</Typography>
                  <Select
                    id="frequency"
                    variant="outlined"
                    name={'frequency'}
                    value={frequency}
                    fullWidth
                    displayEmpty
                    className={styles.frequencySelect}
                    onChange={(e) => setFrequency(e.target.value as string)}
                  >
                    <MenuItem value="" disabled>
                      {t('selectFrequency')}
                    </MenuItem>
                    <MenuItem value={'morning'}>{t('morning')}</MenuItem>
                    <MenuItem value={'afternoon'}>{t('afternoon')}</MenuItem>
                    <MenuItem value={'night'}>{t('night')}</MenuItem>
                  </Select>
                </Box>

                <Box className={`${styles.buttonsContainer} ${styles.buttonsContainerModal}`}>
                  <Box
                    className={styles.buttonSpacing}
                    sx={{
                      opacity: isSubmitting ? 0.5 : 1,
                      pointerEvents: isSubmitting ? 'none' : 'auto',
                    }}
                  >
                    <AppButton
                      theme={ButtonTheme.NewPrimary}
                      type={'submit'}
                      label={props.trainingID ? t('save') : t('add')}
                      handler={() => {}}
                    />
                  </Box>
                  <AppButton
                    theme={ButtonTheme.NewSecondary}
                    type={'button'}
                    label={t('cancel')}
                    handler={handleBack}
                  />
                </Box>
              </Box>
            </form>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
